import React from 'react';
import {useNavigate, useParams} from "react-router-dom";
import {useMutation, useQuery} from "react-query";
import ngage from "services/ngage";
import {useNotifications} from "context/NotificationContext";
import {FormikHelpers} from "formik";
import {invalidate} from "services/query-client";
import Page from "components/Page";
import {ZoneData} from "types/Zone";
import ZoneForm from "pages/Zone/Form";
import {useOperator} from "context/OperatorContext";
import {useAuth} from "../../../context/AuthContext";
import NotAuthorised from "../../NotAuthorised";

function MutateZone() {
  const {termFor} = useOperator();
  const auth = useAuth();
  const {notify} = useNotifications();
  const navigate = useNavigate();
  const {id} = useParams();

  const {data: zone, isLoading} = useQuery(
    ['zones', {id}],
    () => ngage.zones.get(Number(id)),
    {enabled: !!id && auth.can('zones:read')}
  )

  const store = useMutation((data: ZoneData) => ngage.zones.store(data))
  const update = useMutation((data: ZoneData) => ngage.zones.update(Number(id), data))

  // Handle form submission
  async function handleSubmit(values: ZoneData, {setSubmitting}: FormikHelpers<ZoneData>) {
    id ? await handleUpdate(values) : await handleStore(values)
    await invalidate('zones.all')

    setSubmitting(false)
    navigate('/zones')
  }

  // Handle store mutation
  async function handleStore(values: ZoneData) {
    try {
      await store.mutateAsync(values)
      notify("success", `${termFor('Zone')} Created`, `${values.name} has been created`)
    } catch (error) {
      notify("error", `${termFor('Zone')} Not Created`, `${values.name} could not be stored`)
      throw error
    }
  }

  // Handle update mutation
  async function handleUpdate(values: ZoneData) {
    try {
      await update.mutateAsync(values)
      notify("success", `${termFor('Zone')} Updated`, `${values.name} has been updated`)
      await invalidate(['zones', {id}])
    } catch (error) {
      notify("error", `${termFor('Zone')} Not Updated`, `${values.name} could not be updated`)
      throw error
    }
  }

  // Prevent unauthorised mutating
  if (auth.cannot('zones:write')) {
    return <NotAuthorised/>
  }

  // Prepare breadcrumbs
  const newBreadcrumbs = {
    'Operations': '/',
    [termFor('Zones')]: '/zones',
    [`New ${termFor('Zone')}`]: '/zones/new'
  }
  const editBreadcrumbs = {
    'Operations': '/',
    [termFor('Zones')]: '/zones',
    [`Edit ${termFor('Zone')}`]: `/zones/${id}/edit`
  }

  return (
    <Page
      title={`${id ? 'Edit' : 'New'} ${termFor('Zone')}`}
      breadcrumbs={id ? editBreadcrumbs : newBreadcrumbs}
      isLoading={isLoading}
    >
      <ZoneForm zone={zone} onSubmit={handleSubmit}/>
    </Page>
  );
}

export default MutateZone;